/**
 * Find / replace in a single buffer (Scintilla find stack subset, PROJECT §16.3).
 */

function isWordChar(c: string): boolean {
  return /[0-9A-Za-z_]/.test(c);
}

function isWholeWordAt(text: string, start: number, len: number): boolean {
  const before = start > 0 ? text[start - 1]! : " ";
  const after = start + len < text.length ? text[start + len]! : " ";
  return !isWordChar(before) && !isWordChar(after);
}

export function findNextIndex(
  text: string,
  needle: string,
  fromIndex: number,
  caseSensitive: boolean,
  wholeWord = false,
): number {
  if (!needle) return -1;
  const hay = caseSensitive ? text : text.toLowerCase();
  const n = caseSensitive ? needle : needle.toLowerCase();
  let i = Math.max(0, fromIndex);
  while (i <= hay.length - n.length) {
    const j = hay.indexOf(n, i);
    if (j < 0) return -1;
    if (!wholeWord || isWholeWordAt(text, j, needle.length)) return j;
    i = j + 1;
  }
  return -1;
}

export function findPrevIndex(
  text: string,
  needle: string,
  beforeIndex: number,
  caseSensitive: boolean,
  wholeWord = false,
): number {
  if (!needle) return -1;
  const end = Math.min(beforeIndex, text.length);
  const hay = caseSensitive ? text : text.toLowerCase();
  const n = caseSensitive ? needle : needle.toLowerCase();
  const slice = hay.slice(0, end);
  let i = slice.lastIndexOf(n);
  while (i >= 0) {
    if (!wholeWord || isWholeWordAt(text, i, needle.length)) return i;
    i = slice.lastIndexOf(n, i - 1);
  }
  return -1;
}

/** Count non-overlapping matches (whole-word uses same rules as find). */
export function countOccurrences(
  text: string,
  needle: string,
  caseSensitive: boolean,
  wholeWord = false,
): number {
  if (!needle) return 0;
  let n = 0;
  let from = 0;
  const step = Math.max(1, needle.length);
  while (from <= text.length) {
    const i = findNextIndex(text, needle, from, caseSensitive, wholeWord);
    if (i < 0) break;
    n++;
    from = i + step;
  }
  return n;
}

export function replaceFirstOccurrence(
  text: string,
  find: string,
  replace: string,
  caseSensitive: boolean,
  wholeWord = false,
): { text: string; replaced: boolean; selectionStart: number; selectionEnd: number } {
  const idx = findNextIndex(text, find, 0, caseSensitive, wholeWord);
  if (idx < 0) return { text, replaced: false, selectionStart: 0, selectionEnd: 0 };
  const actualLen = find.length;
  const before = text.slice(0, idx);
  const after = text.slice(idx + actualLen);
  const next = before + replace + after;
  return {
    text: next,
    replaced: true,
    selectionStart: idx,
    selectionEnd: idx + replace.length,
  };
}
